import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Badge,
  Text,
  BlockStack,
  InlineStack,
  EmptyState,
  Spinner,
  Modal,
  Toast,
  TextField,
  Select,
  Box,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

const TYPE_LABELS = {
  text: 'Text Box',
  textarea: 'Text Area',
  number: 'Number',
  dropdown: 'Dropdown',
  radio: 'Radio Buttons',
  checkbox: 'Checkbox',
  multiselect: 'Multi-Select',
  color: 'Color Swatch',
  date: 'Date Picker',
  file: 'File Upload',
  toggle: 'Toggle',
  list: 'List',
};

const PER_PAGE_OPTIONS = [
  { label: '5 per page', value: '5' },
  { label: '10 per page', value: '10' },
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
];

function PaginationBar({ page, totalPages, total, limit, start, end, onPrev, onNext }) {
  if (total === 0) return null;
  // Always show so user can change per-page even on single page
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <Text variant="bodySm" tone="subdued">
          Showing {start}–{end} of {total} item{total !== 1 ? 's' : ''}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onPrev} disabled={page <= 1} style={btnStyle(page > 1)}>← Previous</button>
          <Text variant="bodySm" tone="subdued">Page {page} of {totalPages}</Text>
          <button onClick={onNext} disabled={page >= totalPages} style={btnStyle(page < totalPages)}>Next →</button>
        </div>
      </div>
    </Card>
  );
}

function btnStyle(active) {
  return {
    padding: '6px 14px',
    border: '1px solid #c9cccf',
    borderRadius: '6px',
    background: active ? '#fff' : '#f6f6f7',
    color: active ? '#202223' : '#8c9196',
    cursor: active ? 'pointer' : 'not-allowed',
    fontSize: '13px',
    fontFamily: 'sans-serif',
  };
}

export default function AttributesPage() {
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const fetchAttributes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attributes');
      setAttributes(res.data?.data || []);
    } catch (err) {
      setToast({ content: 'Failed to load attributes', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAttributes(); }, [fetchAttributes]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [searchValue, typeFilter, limit]);

  const handleDelete = async () => {
    try {
      await api.delete(`/api/attributes/${deleteModal.id}`);
      setToast({ content: `"${deleteModal.name}" deleted successfully` });
      setDeleteModal(null);
      fetchAttributes();
    } catch (err) {
      setToast({ content: 'Failed to delete attribute', error: true });
    }
  };

  const handleToggleStatus = async (attr) => {
    try {
      await api.patch(`/api/attributes/${attr.id}`, { is_active: !attr.is_active });
      setToast({ content: `Attribute ${attr.is_active ? 'deactivated' : 'activated'}` });
      fetchAttributes();
    } catch (err) {
      setToast({ content: 'Failed to update status', error: true });
    }
  };

  const filtered = attributes.filter(a => {
    const matchSearch = !searchValue || a.label.toLowerCase().includes(searchValue.toLowerCase()) || a.name.toLowerCase().includes(searchValue.toLowerCase());
    const matchType = !typeFilter || a.type === typeFilter;
    return matchSearch && matchType;
  });

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const safePage = Math.min(page, totalPages);
  const start = filtered.length === 0 ? 0 : (safePage - 1) * limit + 1;
  const end = Math.min(safePage * limit, filtered.length);
  const paginated = filtered.slice((safePage - 1) * limit, safePage * limit);

  const rows = paginated.map(attr => [
    <Text variant="bodyMd" fontWeight="semibold">{attr.label}</Text>,
    <Text variant="bodySm" tone="subdued">{attr.name}</Text>,
    <Badge>{TYPE_LABELS[attr.type] || attr.type}</Badge>,
    attr.group_name ? <Badge tone="info">{attr.group_name}</Badge> : <Text tone="subdued">—</Text>,
    attr.is_required ? <Badge tone="critical">Required</Badge> : <Badge>Optional</Badge>,
    <Badge tone={attr.is_active ? 'success' : 'critical'}>{attr.is_active ? 'Active' : 'Inactive'}</Badge>,
    <InlineStack gap="200">
      <Button size="slim" onClick={() => navigate(`/attributes/${attr.id}/edit`)}>Edit</Button>
      <Button size="slim" onClick={() => handleToggleStatus(attr)}>
        {attr.is_active ? 'Deactivate' : 'Activate'}
      </Button>
      <Button size="slim" tone="critical" onClick={() => setDeleteModal(attr)}>Delete</Button>
    </InlineStack>,
  ]);

  const typeOptions = [
    { label: 'All Types', value: '' },
    ...Object.entries(TYPE_LABELS).map(([value, label]) => ({ label, value })),
  ];

  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title="Attributes"
        subtitle="Define custom product options for your configurator"
        primaryAction={{
          content: 'Create Attribute',
          onAction: () => navigate('/attributes/new'),
        }}
      >
        <Layout>
          {/* Filters + per-page */}
          <Layout.Section>
            <Card>
              <InlineStack gap="300" wrap blockAlign="center">
                <div style={{ flex: 1, minWidth: 200 }}>
                  <TextField
                    label=""
                    placeholder="Search attributes..."
                    value={searchValue}
                    onChange={setSearchValue}
                    clearButton
                    onClearButtonClick={() => setSearchValue('')}
                    autoComplete="off"
                  />
                </div>
                <div style={{ minWidth: 180 }}>
                  <Select
                    label=""
                    options={typeOptions}
                    value={typeFilter}
                    onChange={setTypeFilter}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <Select
                    label=""
                    options={PER_PAGE_OPTIONS}
                    value={String(limit)}
                    onChange={v => setLimit(Number(v))}
                  />
                </div>
                <Text variant="bodySm" tone="subdued">
                  {filtered.length} attribute{filtered.length !== 1 ? 's' : ''}
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Table */}
          <Layout.Section>
            <Card padding="0">
              {loading ? (
                <Box padding="800">
                  <div style={{ display: 'flex', justifyContent: 'center' }}><Spinner size="large" /></div>
                </Box>
              ) : filtered.length === 0 ? (
                <EmptyState
                  heading="No attributes found"
                  action={{ content: 'Create Attribute', onAction: () => navigate('/attributes/new') }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Create attributes to define custom options for your products.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Label', 'Name', 'Type', 'Group', 'Required', 'Status', 'Actions']}
                  rows={rows}
                  hoverable
                />
              )}
            </Card>
          </Layout.Section>

          {/* Pagination */}
          {!loading && (
            <Layout.Section>
              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                total={filtered.length}
                limit={limit}
                start={start}
                end={end}
                onPrev={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => Math.min(totalPages, p + 1))}
              />
            </Layout.Section>
          )}
        </Layout>

        {/* Delete Confirmation Modal */}
        <Modal
          open={!!deleteModal}
          onClose={() => setDeleteModal(null)}
          title="Delete Attribute"
          primaryAction={{ content: 'Delete', destructive: true, onAction: handleDelete }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteModal(null) }]}
        >
          <Modal.Section>
            <Text>
              Are you sure you want to delete <strong>{deleteModal?.label}</strong>?
              This will remove it from all associated products.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  );
}
